import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Clock } from "lucide-react";
import { addDays, format, startOfWeek, isSameDay, parseISO } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { api } from "../../lib/api";
import { Card, PageHeader, EmptyState, Spinner, StatusPill } from "../../components/dashboard/DashboardShell";

export default function SchedulePage() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selected, setSelected] = useState(new Date());
  const { data, isLoading } = useQuery({ queryKey: ["orders", "dijadwalkan,sedang_inspeksi,menunggu_konfirmasi"], queryFn: () => api.get("/orders", { params: { status: "dijadwalkan,sedang_inspeksi,menunggu_konfirmasi" } }).then((r) => r.data) });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const forDay = (d) => (data || []).filter((o) => isSameDay(parseISO(o.scheduled_date), d)).sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
  const dayOrders = forDay(selected);
  return (
    <div>
      <PageHeader title="Jadwal Saya" subtitle="Kalender inspeksi yang dijadwalkan dan menunggu konfirmasi." />
      <Card>
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-2 rounded-lg hover:bg-slate-50" data-testid="prev-week-btn"><ChevronLeft size={18} /></button>
          <p className="text-sm font-extrabold text-slate-900">{format(weekStart, "d MMM", { locale: idLocale })} – {format(addDays(weekStart, 6), "d MMM yyyy", { locale: idLocale })}</p>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-2 rounded-lg hover:bg-slate-50" data-testid="next-week-btn"><ChevronRight size={18} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5" data-testid="week-grid">
          {days.map((d) => {
            const n = forDay(d).length;
            const active = isSameDay(d, selected);
            const today = isSameDay(d, new Date());
            return (
              <button key={d} onClick={() => setSelected(d)} data-testid={`day-${format(d, "yyyy-MM-dd")}`}
                className={`rounded-xl py-3 flex flex-col items-center gap-1 border transition-colors ${active ? "bg-blue-600 text-white border-blue-600" : today ? "border-blue-300 bg-blue-50/50 text-slate-800" : "border-slate-100 hover:border-blue-200 text-slate-700"}`}>
                <span className={`text-[10px] font-bold uppercase ${active ? "text-blue-100" : "text-slate-400"}`}>{format(d, "EEE", { locale: idLocale })}</span>
                <span className="text-lg font-extrabold">{format(d, "d")}</span>
                <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${n ? (active ? "bg-white text-blue-600" : "bg-blue-600 text-white") : "opacity-0"}`}>{n}</span>
              </button>
            );
          })}
        </div>
      </Card>
      <Card title={`Jadwal ${format(selected, "EEEE, d MMMM yyyy", { locale: idLocale })}`} className="mt-4">
        {isLoading ? <Spinner /> : !dayOrders.length ? <EmptyState icon="calendar" title="Tidak ada jadwal" desc="Tidak ada inspeksi pada hari ini." /> : (
          <div className="space-y-0" data-testid="day-schedule">
            {dayOrders.map((o, i) => (
              <div key={o.order_id} className="flex gap-4">
                <div className="flex flex-col items-center"><span className="w-2.5 h-2.5 rounded-full bg-blue-600 mt-2" />{i < dayOrders.length - 1 && <span className="w-px flex-1 bg-slate-200" />}</div>
                <button onClick={() => navigate(`/inspector/pesanan/${o.order_id}`)} className="flex-1 text-left pb-5 group">
                  <div className="flex flex-wrap items-center gap-2"><p className="text-sm font-extrabold text-slate-900 flex items-center gap-1.5"><Clock size={14} className="text-blue-600" /> {o.scheduled_time}</p><StatusPill status={o.status} /></div>
                  <p className="text-[13px] font-semibold text-slate-700 mt-1 group-hover:text-blue-600">{o.vehicle_type_label} · {o.vehicle_info?.brand} {o.vehicle_info?.model} · {o.user?.name}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {o.address}, {o.city}</p>
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
